// hooks/useStatements.js
//
// TanStack Query hooks for the account statements (كشف حساب) flow.
// Pattern matches useVendors.js — keys exported for cache consistency,
// useQuery for reads, useMutation for writes that invalidate keys.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createStatement,
    getStatement,
    getStatementPendingRange,
    listStatements,
    previewStatement,
    resendStatementWhatsapp,
    setStatementPayment,
} from "services/statements";

export const statementKeys = {
    all: ["statements"],
    list: (params) => [...statementKeys.all, "list", { params }],
    detail: (id) => [...statementKeys.all, "detail", id],
    pendingRange: (vendorId) => [...statementKeys.all, "pendingRange", vendorId],
};

/**
 * The date span of a vendor's outstanding (unbilled) orders — what the
 * send-statement modal opens its "من" field on.
 *
 * `staleTime: 0` on purpose: sending a statement archives the orders it covered,
 * so the outstanding range moves the moment one is issued. A cached range would
 * reopen the modal on a period that is already settled.
 *
 * @param {number|string} vendorId - User.id; the query is disabled without one.
 */
export const useStatementPendingRange = (vendorId, enabled = true) => {
    const query = useQuery({
        queryKey: statementKeys.pendingRange(vendorId),
        queryFn: () => getStatementPendingRange({ audience: "vendor", vendorId }),
        enabled: Boolean(vendorId) && enabled,
        staleTime: 0,
    });

    return {
        earliestOrderDate: query.data?.earliestOrderDate ?? null,
        latestOrderDate: query.data?.latestOrderDate ?? null,
        pendingCount: query.data?.ordersCount ?? 0,
        loading: query.isLoading,
        error: query.error?.message ?? null,
    };
};

/**
 * List statements with optional filters.
 *
 * @param {object} params Query params (audience, vendorId, dates, cityId, page, size)
 */
export const useListStatements = (params = {}) => {
    const query = useQuery({
        queryKey: statementKeys.list(params),
        queryFn: () => listStatements(params),
        keepPreviousData: true,
    });

    return {
        statements: query.data?.results ?? [],
        count: query.data?.count ?? 0,
        pages: query.data?.pages ?? 0,
        // For an admin: a paid statement is in the list for this number of
        // days after its payment (api: util/statementVisibility.js).
        paidVisibleDays: query.data?.paidVisibleDays ?? null,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/**
 * Fetch one statement by id (with its included orders).
 */
export const useGetStatement = (id) => {
    const query = useQuery({
        queryKey: statementKeys.detail(id),
        queryFn: () => getStatement(id),
        enabled: Boolean(id),
    });

    return {
        statement: query.data?.statement ?? null,
        orders: query.data?.orders ?? [],
        loading: query.isLoading,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/**
 * Preview totals for a candidate statement. Read-only on the server side
 * — used by the send-statement modal to show numbers and the rendered
 * WhatsApp body before the admin commits.
 */
export const usePreviewStatement = () => {
    return useMutation({
        mutationFn: previewStatement,
    });
};

/**
 * Create + send a statement. On success invalidates the statements list
 * cache and the orders cache (orders move into the statement so the
 * orders page must refetch).
 */
export const useCreateStatement = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createStatement,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: statementKeys.all });
            queryClient.invalidateQueries({ queryKey: ["orders"] });
        },
    });
};

/**
 * Resend the WhatsApp for an existing statement. Updates the persisted
 * send status on the row.
 */
export const useResendStatementWhatsapp = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: resendStatementWhatsapp,
        onSuccess: (_data, id) => {
            queryClient.invalidateQueries({ queryKey: statementKeys.all });
            queryClient.invalidateQueries({ queryKey: statementKeys.detail(id) });
        },
    });
};

/**
 * Toggle a statement's paid/unpaid status. Invalidates the list cache so
 * any visible page refetches with the updated paid badge, and the detail
 * cache for the specific row so the detail page reflects the new state
 * immediately.
 *
 * Usage:
 *   const setPayment = useSetStatementPayment();
 *   setPayment.mutate({ id: 42, isPaid: true });
 */
export const useSetStatementPayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, isPaid }) => setStatementPayment(id, isPaid),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: statementKeys.all });
            queryClient.invalidateQueries({
                queryKey: statementKeys.detail(variables.id),
            });
        },
    });
};
